import { useEffect, useCallback } from 'react';
import { useToast } from './use-toast';
import { useSpeechRecognitionState } from './useSpeechRecognitionState';
import { useSpeechRecognitionConfig } from './useSpeechRecognitionConfig';
import { useAudioPermissions } from './useAudioPermissions';
import { useRecognitionCleanup } from './useRecognitionCleanup';
import { RecognitionHandlers } from '@/types/speechRecognition';

export const useSpeechRecognition = ({
  onFinalTranscript,
  onTranscriptChange,
  isDisabled = false
}: RecognitionHandlers) => {
  const { toast } = useToast();
  const state = useSpeechRecognitionState();
  const { isListening, setIsListening, transcript, setTranscript, recognitionRef } = state;
  const { initializeSpeechRecognition } = useSpeechRecognitionConfig();
  const { requestMicrophonePermission } = useAudioPermissions();
  const { forceCleanup } = useRecognitionCleanup(state);

  const startListening = useCallback(async () => {
    if (isDisabled) {
      console.log('Speech recognition is disabled');
      return;
    }
    
    try {
      await forceCleanup();
      
      const stream = await requestMicrophonePermission();
      if (!stream) {
        await forceCleanup();
        return;
      }

      console.log('Starting speech recognition...');
      const recognition = initializeSpeechRecognition();
      if (!recognition) {
        console.error('Speech recognition not supported');
        toast({
          title: "No soportado",
          description: "El reconocimiento de voz no está soportado en este navegador.",
          variant: "destructive"
        });
        return;
      }

      recognition.onstart = () => {
        console.log('Speech recognition started');
        setIsListening(true);
        setTranscript('');
      };

      recognition.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        const currentTranscript = finalTranscript || interimTranscript;
        if (currentTranscript) {
          onTranscriptChange?.(currentTranscript);
          setTranscript(currentTranscript);
        }
      };

      recognition.onerror = async (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'not-allowed') {
          toast({
            title: "Acceso denegado",
            description: "No se pudo acceder al micrófono. Por favor, verifica los permisos.",
            variant: "destructive"
          });
        }
        await forceCleanup();
      };

      recognition.onend = () => {
        console.log('Speech recognition ended');
        if (!isListening) {
          forceCleanup();
        }
      };

      recognition.onspeechend = () => {
        console.log('Speech has stopped being detected');
      };

      recognitionRef.current = recognition;

      try {
        recognition.start();
      } catch (error) {
        console.error('Error starting recognition:', error);
        toast({
          title: "Error",
          description: "No se pudo iniciar el reconocimiento de voz. Por favor, intenta de nuevo.",
          variant: "destructive"
        });
        await forceCleanup();
      }
    } catch (error) {
      console.error('Error in startListening:', error);
      toast({
        title: "Error",
        description: "Hubo un problema al iniciar el reconocimiento de voz. Por favor, recarga la página.",
        variant: "destructive"
      });
      await forceCleanup();
    }
  }, [forceCleanup, initializeSpeechRecognition, isDisabled, setIsListening, setTranscript, toast, onTranscriptChange, requestMicrophonePermission, isListening]);

  const stopListening = useCallback(async () => {
    console.log('Manually stopping speech recognition...');
    if (transcript) {
      console.log('Processing transcript on stop:', transcript);
      onFinalTranscript(transcript.trim());
    }
    await forceCleanup();
  }, [forceCleanup, onFinalTranscript, transcript]);

  useEffect(() => {
    return () => {
      forceCleanup();
    };
  }, [forceCleanup]);

  return {
    isListening,
    transcript,
    startListening,
    stopListening,
  };
};